import { createAsyncThunk } from "@reduxjs/toolkit";
import { chatService } from "../services/chat.service";
import { handleRejectError } from "../../../utils/api/api.utils";
import { ApiError } from "../../../utils/api/api.service";
import {
  Conversation,
  Message,
  SendMessageData,
  ConversationWithMessages,
  FetchMessagesParams,
  FetchMessagesApiResponse,
  PaginationMetadata,
  ListConversationsApiResponse,
} from "../interfaces/chat.interface";

export const CHAT_CONVERSATIONS_PAGE_LIMIT = 15;

function parseConversationsListResponse(
  raw: ListConversationsApiResponse,
  page: number,
  limit: number,
): { conversations: Conversation[]; pagination: PaginationMetadata } | null {
  if (!raw.success) return null;

  if (Array.isArray(raw.data)) {
    const conversations = raw.data;
    const pagination =
      raw.pagination ?? {
        total: conversations.length,
        page,
        limit,
        totalPages: conversations.length > 0 ? 1 : 0,
      };
    return { conversations, pagination };
  }

  if (raw.data && typeof raw.data === "object" && "conversations" in raw.data) {
    const nested = raw.data as {
      conversations: Conversation[];
      pagination?: PaginationMetadata;
    };
    const conversations = nested.conversations ?? [];
    const pagination =
      nested.pagination ??
      raw.pagination ?? {
        total: conversations.length,
        page,
        limit,
        totalPages: conversations.length > 0 ? 1 : 0,
      };
    return { conversations, pagination };
  }

  return null;
}

function getErrorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message || "An unexpected error occurred";
  return "An unexpected error occurred";
}

export type FetchConversationsParams = {
  page?: number;
  limit?: number;
  append?: boolean;
};

// Fetch conversations (paginated; append for infinite scroll)
export const fetchConversations = createAsyncThunk<
  {
    conversations: Conversation[];
    pagination: PaginationMetadata;
    append: boolean;
  },
  FetchConversationsParams | undefined
>("chat/fetchConversations", async (arg, { rejectWithValue }) => {
  const page = arg?.page ?? 1;
  const limit = arg?.limit ?? CHAT_CONVERSATIONS_PAGE_LIMIT;
  const append = !!arg?.append;
  try {
    const response = (await chatService.getConversations(
      page,
      limit,
    )) as ListConversationsApiResponse;

    const parsed = parseConversationsListResponse(response, page, limit);
    if (parsed) {
      return { ...parsed, append };
    }
    return rejectWithValue(
      response.message || "Failed to fetch conversations",
    );
  } catch (error: any) {
    return handleRejectError(rejectWithValue, error);
  }
});

// Fetch specific conversation metadata and initial messages
export const fetchConversationById = createAsyncThunk<
  ConversationWithMessages & { currentUserId?: string | null },
  { conversationId: string; currentUserId?: string | null } | string
>("chat/fetchConversationById", async (arg, { rejectWithValue }) => {
  try {
    const conversationId = typeof arg === 'string' ? arg : arg.conversationId;
    const currentUserId = typeof arg === 'object' ? arg.currentUserId : null;

    const metaResponse =
      await chatService.getConversationMetadata(conversationId);
    const messagesResponse = await chatService.getConversation(conversationId, {
      page: 1,
      limit: 20,
    });

    const messagesRaw = messagesResponse as unknown as FetchMessagesApiResponse | { success?: boolean; data?: any };
    const messages = Array.isArray((messagesRaw as any)?.data)
      ? ((messagesRaw as any).data as Message[])
      : Array.isArray((messagesResponse as any)?.data)
        ? ((messagesResponse as any).data as Message[])
        : [];

    if (metaResponse.success && (messagesRaw as any)?.success) {
      return {
        conversation: metaResponse.data,
        messages,
        currentUserId,
      };
    }
    return rejectWithValue("Failed to fetch conversation details");
  } catch (error: any) {
    return handleRejectError(rejectWithValue, error);
  }
});

// Fetch messages (paginated)
export const fetchMessages = createAsyncThunk<
  {
    messages: Message[];
    reset: boolean;
    currentUserId?: string | null;
    page: number;
    pagination: PaginationMetadata;
  },
  FetchMessagesParams
>("chat/fetchMessages", async (params, { rejectWithValue }) => {
  try {
    const page = params.page ?? 1;
    const limit = params.limit ?? 20;
    const response = (await chatService.getConversation(params.conversationId, {
      page,
      limit,
    })) as FetchMessagesApiResponse;

    if (response.success && Array.isArray(response.data) && response.pagination) {
      return {
        messages: response.data,
        reset: !!params.reset,
        currentUserId: params.currentUserId,
        page,
        pagination: response.pagination,
      };
    }
    return rejectWithValue(response.message || "Failed to fetch messages");
  } catch (error: any) {
    return handleRejectError(rejectWithValue, error);
  }
});

// Send message
export const sendMessage = createAsyncThunk<Message & { currentUserId?: string | null }, SendMessageData>(
  "chat/sendMessage",
  async (messageData, { rejectWithValue }) => {
    try {
      const response = await chatService.sendMessage(messageData);
      if (response.success && response.data) {
        return { ...response.data, currentUserId: messageData.currentUserId };
      }
      return rejectWithValue(response.message || "Failed to send message");
    } catch (error: any) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

// Mark message as read
export const markMessageRead = createAsyncThunk(
  "chat/markMessageRead",
  async (messageId: string, { rejectWithValue }) => {
    try {
      const response = await chatService.markMessageAsRead(messageId);
      if (response.success) {
        return { messageId };
      }
      return rejectWithValue(
        response.message || "Failed to mark message as read",
      );
    } catch (error: any) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

// Mark conversation as read
export const markConversationRead = createAsyncThunk(
  "chat/markConversationRead",
  async (conversationId: string, { rejectWithValue }) => {
    try {
      const response = await chatService.markConversationAsRead(conversationId);
      if (response.success) {
        return { conversationId };
      }
      return rejectWithValue(
        response.message || "Failed to mark conversation as read",
      );
    } catch (error: any) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

// Create or get conversation with a participant (e.g. seller)
export const createConversation2 = createAsyncThunk<
  { id: string },
  string,
  { rejectValue: string }
>("chat/createConversation", async (participantId, { rejectWithValue }) => {
  try {
    const response = await chatService.createConversation2(participantId);
    if (response?.success && response?.data?.id) {
      return { id: response.data.id };
    }
    return rejectWithValue(
      (response as { message?: string })?.message || "Failed to create conversation"
    );
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

// Contact request
export const contactRequest = createAsyncThunk<
  any,
  { requestId: string; message: string; productData: any }
>("chat/contactRequest", async (data, { rejectWithValue }) => {
  try {
    const response = await chatService.contactRequest(data);
    if (response.success && response.data) {
      return response.data;
    }
    return rejectWithValue(
      response.message || "Failed to send contact request",
    );
  } catch (error: any) {
    return handleRejectError(rejectWithValue, error);
  }
});

export type FetchProductConversationsParams = {
  page?: number;
  limit?: number;
  append?: boolean;
};

// Fetch conversations for a product (owner view)
export const fetchConversationsByProduct = createAsyncThunk<
  {
    conversations: Conversation[];
    pagination: PaginationMetadata;
    append: boolean;
  },
  { productId: string } & FetchProductConversationsParams
>("chat/fetchConversationsByProduct", async ({ productId, page = 1, limit = 15, append = false }, { rejectWithValue }) => {
  try {
    const response = await chatService.getConversationsByProduct(productId, page, limit);

    if (response.success && response.data) {
      let conversations: Conversation[] = [];
      let pagination: PaginationMetadata;

      if (Array.isArray(response.data)) {
        conversations = response.data;
        pagination = response.pagination ?? {
          total: conversations.length,
          page,
          limit,
          totalPages: conversations.length > 0 ? 1 : 0,
        };
      } else if (response.data && typeof response.data === "object" && "conversations" in response.data) {
        const nested = response.data as {
          conversations: Conversation[];
          pagination?: PaginationMetadata;
        };
        conversations = nested.conversations ?? [];
        pagination = nested.pagination ?? response.pagination ?? {
          total: conversations.length,
          page,
          limit,
          totalPages: conversations.length > 0 ? 1 : 0,
        };
      } else {
        conversations = [];
        pagination = {
          total: 0,
          page,
          limit,
          totalPages: 0,
        };
      }

      return { conversations, pagination, append };
    }
    return rejectWithValue(
      response.message || "Failed to fetch product conversations",
    );
  } catch (error: any) {
    return handleRejectError(rejectWithValue, error);
  }
});

// Create conversation (contact seller from product detail)
export const createConversation = createAsyncThunk<
  any,
  { participantId: string; productId: string; productData: any }
>("chat/createConversation", async (data, { rejectWithValue }) => {
  try {
    const response = await chatService.createConversation(data);
    if (response.success && response.data) {
      return response.data;
    }
    return rejectWithValue(response.message || "Failed to create conversation");
  } catch (error: any) {
    return handleRejectError(rejectWithValue, error);
  }
});
